import axios from 'axios';
import { API_ENDPOINTS } from '../constants/api';
import { AUTH_EVENT, AUTH_LOGOUT_REASON, AUTH_STORAGE, LogoutReason } from '../constants/auth';
import { APP_ROUTES } from '../constants/routes';
import type { BookingStatus, BookingType } from '../constants/booking';

// Register token interceptor at module level so it is active before any
// component effect fires (child effects run before parent effects in React).
axios.interceptors.request.use(config => {
  const t = localStorage.getItem(AUTH_STORAGE.TOKEN);
  if (t) config.headers.set('Authorization', `Bearer ${t}`);
  return config;
});

const isLoginRequest = (url?: string) => !!url && url.includes(API_ENDPOINTS.AUTH.LOGIN);

axios.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401 && !isLoginRequest(err.config?.url)) {
      const reason = (err.response.data?.reason || AUTH_LOGOUT_REASON.INVALID_TOKEN) as LogoutReason;
      localStorage.removeItem(AUTH_STORAGE.TOKEN);
      localStorage.removeItem(AUTH_STORAGE.USER);
      sessionStorage.setItem(AUTH_STORAGE.LOGOUT_REASON, reason || AUTH_LOGOUT_REASON.INVALID_TOKEN);
      window.dispatchEvent(new CustomEvent(AUTH_EVENT.LOGOUT, { detail: { reason } }));
      if (window.location.pathname !== APP_ROUTES.LOGIN) {
        window.location.assign(APP_ROUTES.LOGIN);
      }
    }
    return Promise.reject(err);
  }
);

// ---- Types ---------------------------------------------------------------

export type RoomType =
  | 'Suite Room'
  | 'King Room'
  | 'Super Deluxe Cottage'
  | 'Deluxe Cottage'
  | 'Mud Cottage'
  | 'Big Tent'
  | 'Small Tent'
  | 'Other';

export interface RoomBooking {
  ROOM_BOOKING_ID?: number;
  DAY_BOOKING_ID?: number;
  ROOM_TYPE: RoomType;
  CUSTOM_ROOM_TYPE?: string;
  ROOM_NUMBER: string;
  NO_OF_ROOMS: number;
  NO_ADULTS_IN_ROOM?: number;
  NO_CHILDREN_IN_ROOM?: number;
  AMOUNT_PER_PERSON: number;
}

export interface DayBooking {
  DAY_BOOKING_ID?: number;
  BOOKINGID?: number;
  STAY_DATE: string;
  DRIVER_AMOUNT_PER_DAY?: number;
  rooms: RoomBooking[];
}

export interface Booking {
  BOOKINGID?: number;
  GUEST_NAME: string;
  PHONE: string;
  EMAIL?: string;
  ADDRESS?: string;
  CHECKIN_DATE: string;
  CHECKOUT_DATE: string;
  BOOKING_TYPE?: BookingType;
  NO_ADULTS: number;
  NO_CHILDREN: number;
  NO_DRIVERS: number;
  ADVANCE_AMOUNT: number;
  TOTAL_AMOUNT: number;
  SYSTEM_CALCULATED_TOTAL?: number;
  AMOUNT_DIFFERENCE?: number;
  AMOUNT_OVERRIDE_REASON?: string | null;
  AMOUNT_OVERRIDE_NOTE?: string | null;
  BOOKED_BY?: string;
  REMARKS?: string;
  STATUS: BookingStatus;
  CANCELLATION_REASON?: string | null;
  IS_REFUNDED?: 'yes' | 'no' | null;
  REFUNDED_AMOUNT?: number | null;
  CANCELLED_ON?: string | null;
  RESCHEDULED_ON?: string | null;
  CREATEDON?: string;
  CREATEDBY?: string;
  LASTMODIFIEDON?: string;
  LASTMODIFIEDBY?: string;
  ROOM_SUMMARY?: string;
  dayBookings?: DayBooking[];
}

export interface AvailableRooms {
  available: Record<string, string[]>;
  booked: string[];
}

export const ROOM_TYPES: RoomType[] = [
  'Suite Room',
  'King Room',
  'Super Deluxe Cottage',
  'Deluxe Cottage',
  'Mud Cottage',
  'Big Tent',
  'Small Tent',
  'Other',
];

export const ROOM_INVENTORY: Record<string, string[]> = {
  'Suite Room': ['401', '402'],
  'King Room': ['403'],
  'Super Deluxe Cottage': ['201', '202', '203', '204', '205', '206'],
  'Deluxe Cottage': ['301', '302', '303', '304', '305', '306'],
  'Mud Cottage': ['101','102','103','104','105','106','107','108','109','110','111','112','113'],
  'Big Tent': ['BT-1', 'BT-2', 'BT-3'],
  'Small Tent': ['ST-1', 'ST-2', 'ST-3', 'ST-4', 'ST-5', 'ST-6', 'ST-7', 'ST-8'],
  'Other': [],
};

// Legacy export kept for compatibility
export type Guest = Booking;

// ---- API Functions -------------------------------------------------------

function coerceBooking(b: any): Booking {
  b.TOTAL_AMOUNT = parseFloat(b.TOTAL_AMOUNT) || 0;
  b.SYSTEM_CALCULATED_TOTAL = parseFloat(b.SYSTEM_CALCULATED_TOTAL) || 0;
  b.AMOUNT_DIFFERENCE = parseFloat(b.AMOUNT_DIFFERENCE) || 0;
  b.ADVANCE_AMOUNT = parseFloat(b.ADVANCE_AMOUNT) || 0;
  b.NO_ADULTS = parseInt(b.NO_ADULTS) || 0;
  b.NO_CHILDREN = parseInt(b.NO_CHILDREN) || 0;
  b.NO_DRIVERS = parseInt(b.NO_DRIVERS) || 0;
  if (b.REFUNDED_AMOUNT != null) b.REFUNDED_AMOUNT = parseFloat(b.REFUNDED_AMOUNT) || 0;
  if (b.dayBookings) {
    b.dayBookings.forEach((d: any) => {
      d.DRIVER_AMOUNT_PER_DAY = parseFloat(d.DRIVER_AMOUNT_PER_DAY) || 0;
      if (d.rooms) d.rooms.forEach((r: any) => {
        r.AMOUNT_PER_PERSON = parseFloat(r.AMOUNT_PER_PERSON) || 0;
        r.NO_OF_ROOMS = parseInt(r.NO_OF_ROOMS) || 1;
        r.NO_ADULTS_IN_ROOM = parseInt(r.NO_ADULTS_IN_ROOM) || 0;
        r.NO_CHILDREN_IN_ROOM = parseInt(r.NO_CHILDREN_IN_ROOM) || 0;
      });
    });
  }
  return b as Booking;
}

export const getGuests = async (filters?: {
  date?: string;
  name?: string;
  phone?: string;
  email?: string;
  status?: string;
}): Promise<Booking[]> => {
  const response = await axios.get(API_ENDPOINTS.GUESTS.LIST, { params: filters });
  return (response.data.data as any[]).map(coerceBooking);
};

export const getGuestById = async (id: number): Promise<Booking> => {
  const response = await axios.get(API_ENDPOINTS.GUESTS.BY_ID(id));
  return coerceBooking(response.data.data);
};

export const getAuditTrail = async (id: number): Promise<any[]> => {
  const response = await axios.get(API_ENDPOINTS.GUESTS.AUDIT(id));
  return response.data.data;
};

export const getAvailableRooms = async (date: string, excludeBookingId?: number): Promise<AvailableRooms> => {
  const params: any = { date };
  if (excludeBookingId) params.excludeBookingId = excludeBookingId;
  const response = await axios.get(API_ENDPOINTS.GUESTS.AVAILABLE_ROOMS, { params });
  return response.data.data;
};

export const createGuest = async (booking: Booking): Promise<{ id: number }> => {
  const response = await axios.post(API_ENDPOINTS.GUESTS.LIST, booking);
  return response.data.data;
};

export const updateGuest = async (id: number, booking: Partial<Booking>): Promise<void> => {
  await axios.put(API_ENDPOINTS.GUESTS.BY_ID(id), booking);
};

export const deleteGuest = async (id: number): Promise<void> => {
  await axios.delete(API_ENDPOINTS.GUESTS.BY_ID(id));
};

// ---- Document Types & Functions ------------------------------------------

export interface BookingDocument {
  id: number;
  booking_id: number;
  filename: string;
  original_name: string;
  mime_type: string;
  file_size: number;
  uploaded_by: string;
  uploaded_at: string;
}

export const getDocuments = async (bookingId: number): Promise<BookingDocument[]> => {
  const response = await axios.get(API_ENDPOINTS.GUESTS.DOCUMENTS(bookingId));
  return response.data.data;
};

export const uploadDocuments = async (bookingId: number, files: File[]): Promise<BookingDocument[]> => {
  const formData = new FormData();
  files.forEach(f => formData.append('documents', f));
  const response = await axios.post(API_ENDPOINTS.GUESTS.DOCUMENTS(bookingId), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

export const deleteDocument = async (bookingId: number, docId: number): Promise<void> => {
  await axios.delete(API_ENDPOINTS.GUESTS.DOCUMENT_BY_ID(bookingId, docId));
};

// ---- Notifications -------------------------------------------------------

export const sendBookingWhatsApp = async (id: number, pdfBase64: string): Promise<{ message: string; pdfError?: string }> => {
  const res = await axios.post(API_ENDPOINTS.NOTIFY.WHATSAPP(id), { pdfBase64 });
  return { message: res.data.message, pdfError: res.data.pdfError };
};

export const sendBookingEmail = async (id: number, pdfBase64: string): Promise<void> => {
  await axios.post(API_ENDPOINTS.NOTIFY.EMAIL(id), { pdfBase64 });
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  await axios.put(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
    current_password: currentPassword,
    new_password: newPassword,
  });
};
